/**
 * Tells a fresh document load apart from a backwards navigation.
 *
 * React Router reports POP for both — the initial render after a reload or a typed URL
 * looks exactly like a Back press. The Navigation Timing API carries the missing half:
 * how the *document* was loaded. Combining the two, POP means "the user went backwards"
 * everywhere except the initial render of a document that was loaded fresh.
 *
 * A `back_forward` load is deliberately *not* fresh: it means the document itself was
 * reached by a history traversal (a Back press restoring a page from the bfcache), which
 * is the very case the suppression exists for. Browsers also report `back_forward` for a
 * restored tab, which is a resume rather than a Back press — it lands on the selection
 * screen instead of fast-forwarding, which is the safe direction to err in.
 */

const NAVIGATION_ENTRY = typeof performance === 'undefined'
  ? undefined
  : performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

const DOCUMENT_LOAD_TYPE = NAVIGATION_ENTRY?.type ?? 'navigate';

let documentSettled = false;

/** Called once the app has rendered, after which every POP is a real backwards move. */
export function markDocumentSettled(): void {
  documentSettled = true;
}

export function isFreshDocumentLoad(): boolean {
  return !documentSettled && DOCUMENT_LOAD_TYPE !== 'back_forward';
}
